"use client";

import { useSearchParams } from "next/navigation";

import SearchBar from "../components/SearchBar";
import ErrorMessage from "../components/ErrorMessage";
import ResultsFor from "../components/ResultFor";
import Posts from "../components/Posts";
import Pagination from "../components/Pagination";
import type { FormattedPost } from "@/lib/blog";
import isMobile from "is-mobile";
import { Tag } from "../types";

const MOBILE_PAGE_SIZE = 3;
const DESKTOP_PAGE_SIZE = 12;

const stripHtml = (value = "") => value.replace(/<[^>]+>/g, " ").toLowerCase();

const normalizeString = (value?: string | null) =>
  value ? value.toLowerCase().trim() : "";

const filterPosts = (
  posts: FormattedPost[],
  {
    normalizedSearchTerm,
    tagId,
  }: { normalizedSearchTerm: string; tagId?: string },
) => {
  if (!normalizedSearchTerm && !tagId) {
    return posts;
  }

  return posts.filter((post) => {
    const matchesSearch = normalizedSearchTerm
      ? [
          stripHtml(post.title ?? ""),
          stripHtml(post.excerpt ?? ""),
          stripHtml(post.content ?? ""),
        ].some((field) => field.includes(normalizedSearchTerm))
      : true;

    const matchesTag = tagId
      ? post.tags?.some((tag) => tag.id?.toString() === tagId || tag.slug === tagId)
      : true;

    return matchesSearch && matchesTag;
  });
};

const getResultsLabel = (
  {
    searchTerm,
    slug,
    tagId,
  }: {
    searchTerm?: string | null;
    slug?: string | null;
    tagId?: string | null;
  },
  tags: Tag[],
) => {
  if (tagId) {
    return tags.find((tag) => tag.id?.toString() === tagId || tag.slug === tagId)?.name;
  }

  if (slug) {
    return slug;
  }

  if (searchTerm) {
    return searchTerm;
  }

  return undefined;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const BlogContent = ({
  posts,
  tags,
  initialTag,
  currentPage: serverCurrentPage,
  totalPages: serverTotalPages,
  basePath,
}: BlogContentProps) => {
  const searchParams = useSearchParams();

  const rawSearch = searchParams.get("search");
  const normalizedSearchTerm = normalizeString(rawSearch);
  const tagId =
    searchParams.get("tags") ?? initialTag?.id?.toString() ?? undefined;
  const slug = searchParams.get("slug") ?? initialTag?.slug ?? undefined;

  const isServerPagination = basePath && !normalizedSearchTerm && !tagId;

  const currentPageParam = parseInt(searchParams.get("page") ?? "1", 10);
  const pageSize = isMobile() ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

  const filteredPosts = filterPosts(posts, { normalizedSearchTerm, tagId });

  let totalPages = 1;
  let currentPage = 1;
  let paginatedPosts;

  if (isServerPagination) {
    totalPages = serverTotalPages || 1;
    currentPage = serverCurrentPage || 1;
    paginatedPosts = posts;
  } else {
    totalPages = Math.max(1, Math.ceil(filteredPosts.length / pageSize));
    currentPage = clamp(
      Number.isNaN(currentPageParam) ? 1 : currentPageParam,
      1,
      totalPages,
    );
    const start = (currentPage - 1) * pageSize;
    paginatedPosts = filteredPosts.slice(start, start + pageSize);
  }

  const resultsFor = getResultsLabel(
    { searchTerm: rawSearch?.trim(), slug, tagId },
    tags,
  );

  if (!filteredPosts.length) {
    return (
      <>
        <SearchBar tags={tags} />
        {resultsFor && <ResultsFor>{resultsFor}</ResultsFor>}
        <ErrorMessage content="No blog posts matched your filters." />
      </>
    );
  }

  return (
    <>
      <SearchBar tags={tags} />
      {resultsFor && <ResultsFor>{resultsFor}</ResultsFor>}
      <Posts posts={paginatedPosts} />
      <Pagination currentPage={currentPage} totalPages={totalPages} />
    </>
  );
};

interface BlogContentProps {
  posts: FormattedPost[];
  tags: Tag[];
  initialTag?: Tag;
  currentPage?: number;
  totalPages?: number;
  basePath?: string;
}

export default BlogContent;
